import { FC, useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@features/ui';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { UserGroup, UserGroupFormData } from '@src/types/userGroups';
import {
  getUserGroups,
  createUserGroup,
  updateUserGroup,
  deleteUserGroup,
} from '@src/services/userGroups';

import { UserGroupFormDialog } from './UserGroupFormDialog';
import { ConfirmDeleteGroupPopup } from './ConfirmDeleteGroupPopup';

interface UserGroupsPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGroupsChange?: () => void;
}

export const UserGroupsPopup: FC<UserGroupsPopupProps> = ({
  open,
  onOpenChange,
  onGroupsChange,
}) => {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');

  // Fetch groups khi popup mở
  useEffect(() => {
    if (open) {
      fetchGroups();
    }
  }, [open]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await getUserGroups();

      setGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching user groups:', error);
      toast.error('Failed to load user groups');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroup = () => {
    setSelectedGroup(null);
    setFormMode('add');
    setFormDialogOpen(true);
  };

  const handleEditGroup = (group: UserGroup) => {
    setSelectedGroup(group);
    setFormMode('edit');
    setFormDialogOpen(true);
  };

  const handleDeleteGroup = (group: UserGroup) => {
    setSelectedGroup(group);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: UserGroupFormData) => {
    try {
      if (formMode === 'add') {
        await createUserGroup(data);
        toast.success('User group created successfully');
      } else if (formMode === 'edit' && selectedGroup) {
        await updateUserGroup(selectedGroup.id, data);
        toast.success('User group updated successfully');
      }

      await fetchGroups();
      if (onGroupsChange) {
        onGroupsChange();
      }
    } catch (error) {
      console.error('Error saving user group:', error);
      toast.error(
        `Failed to ${formMode === 'add' ? 'create' : 'update'} user group`
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedGroup) return;

    try {
      await deleteUserGroup(selectedGroup.id);
      toast.success('User group deleted successfully');
      await fetchGroups();
      setSelectedGroup(null);
      if (onGroupsChange) {
        onGroupsChange();
      }
    } catch (error) {
      console.error('Error deleting user group:', error);
      toast.error('Failed to delete user group');
    }
  };

  // Sắp xếp groups theo order
  const sortedGroups = [...groups].sort((a, b) => a.order - b.order);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[600px] max-w-[90vw] mx-auto max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-2xl text-center mb-[23px]">
              Manage User Groups
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2">
            {loading ? (
              <div className="text-center text-gray-400 py-8">Loading...</div>
            ) : sortedGroups.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                No user groups found. Click &ldquo;Add User Group&rdquo; to
                create one.
              </div>
            ) : (
              <div className="space-y-3">
                {sortedGroups.map((group) => (
                  <div
                    key={group.id}
                    className="border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-base mb-1 truncate">
                          {group.name}
                        </h4>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          className="w-8 h-8"
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditGroup(group)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          className="w-8 h-8"
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteGroup(group)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-700">
            <div className="flex items-center justify-center gap-4">
              <Button
                className="px-6"
                size="sm"
                variant="lime"
                onClick={handleAddGroup}
              >
                Add User Group
              </Button>
              <Button
                className="px-6"
                size="sm"
                variant="secondary"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <UserGroupFormDialog
        initialData={
          selectedGroup
            ? {
                name: selectedGroup.name,
                descriptions: selectedGroup.descriptions,
                order: selectedGroup.order,
              }
            : null
        }
        mode={formMode}
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        onSubmit={handleFormSubmit}
      />

      <ConfirmDeleteGroupPopup
        groupTitle={selectedGroup?.name}
        open={deleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onOpenChange={setDeleteDialogOpen}
      />
    </>
  );
};
